var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-world-map> — THE world map: gateways + private cloud servers on a globe.
 * Lives in base-ui because two surfaces render the SAME map — the fleet app's Map
 * page and the dashboards map widget — and 'exactly the same' is only guaranteed by
 * one component. MapLibre GL (bespoke, MINIMALIST dark basemap — MapLibre GL (bespoke, MINIMALIST dark basemap —
 * keyless: a bundled vector world, no tiles/tokens/infra) + a deck.gl overlay that
 * owns ALL map data on the GPU. The canvas is the only third-party surface; every
 * colour comes from base-ui tokens (theme-aware) and the chrome (legend, hover tip)
 * reuses the app's styles, so the map reads as part of the app, not a widget.
 *
 * MARKERS (DOM — maplibre Markers, NOT a deck layer):
 *   • gateways + cloud servers are the SAME component (.fleet-{gateway,cloud}-chip): a
 *     filled circle + glyph + CSS box-shadow GLOW + CSS heartbeat ping. Only the colour
 *     differs — blue = cloud hub (always on), green = healthy gateway, yellow = in the
 *     offline timeout buffer, red = fully offline (red does NOT pulse). DOM (not a GPU
 *     IconLayer) because a CSS box-shadow glow renders cleanly where a rasterized SVG
 *     halo reads grey. Boxes sharing a location (same public IP → same resolved geo)
 *     COLLAPSE into one cluster chip when zoomed out and FAN onto a phyllotaxis disc at/
 *     above EXPAND_ZOOM (clusterLayout). Click a gateway → its panel; a cluster → zoom in.
 *
 * DATA LAYERS (deck.gl): only the ambient day/night terminator + one-language city labels.
 *
 * BASEMAP: self-hosted Protomaps VECTOR tiles (OpenStreetMap) from one PMTiles archive
 * on our R2 bucket — no key, no third-party quota, zero egress. Symbol layers are
 * dropped so the basemap carries no multi-script place names; city names are a
 * separate ONE-LANGUAGE deck TextLayer (see CITIES). The minimap is a bundled land
 * outline (WORLD_LAND_PATH), not a tile.
 *
 * Light DOM (createRenderRoot → this) so MapLibre's global CSS + the app SCSS reach
 * the map DOM (the legend overlay etc.).
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Protocol as PmtilesProtocol } from 'pmtiles';
import { layers as basemapLayers, namedFlavor } from '@protomaps/basemaps';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { PolygonLayer, TextLayer } from '@deck.gl/layers';
import { token } from '../canvas-tokens';
import { getResolvedTheme, onThemeChange } from '../theme';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { CITIES } from '../map/cities';
import { WORLD_LAND_PATH } from '../map/world-land';
import { geoToLatLng } from '../map/geo';
function hexRGB(hex) {
    const h = hex.replace('#', '');
    const n = h.length === 3 ? h.replace(/(.)/g, '$1$1') : h;
    return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}
/** Escape for the hover-tooltip HTML (names/orgs are admin-set; never trust raw). */
const esc = (s) => s.replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c] ?? c);
/** Min city population to label at a given zoom — only the biggest when zoomed out,
 *  more as you zoom in (the dataset goes down to ~62k pop). */
function popThreshold(z) {
    if (z < 3.5)
        return 6_000_000;
    if (z < 4.5)
        return 2_500_000;
    if (z < 5.5)
        return 1_200_000;
    if (z < 6.5)
        return 600_000;
    if (z < 7.5)
        return 300_000;
    return 150_000; // de-overlap (below) does the real spacing; this just bounds candidates
}
/** The app's body font stack — so the map's city labels match the rest of the UI. */
function appFont() {
    return getComputedStyle(document.body).fontFamily || 'system-ui, sans-serif';
}
/** The night-side polygon (lng/lat ring) for the solar terminator at `date` — the
 *  ambient day/night shade every global-ops map carries. Subsolar point from the day's
 *  declination + the UTC hour; the terminator latitude per longitude is the great circle
 *  90° from it, closed along whichever pole is in darkness. Approximate (no equation of
 *  time) — it's an aesthetic overlay, not navigation. Verified at solstice (Antarctic in
 *  24 h night, Arctic in 24 h day). */
function nightPolygon(date) {
    const rad = Math.PI / 180;
    const dayOfYear = (date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 0)) / 86_400_000;
    const dec = -23.44 * Math.cos((360 / 365) * (dayOfYear + 10) * rad); // solar declination (deg)
    const utcHours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
    const subsolarLng = (12 - utcHours) * 15; // sun overhead at local solar noon (~0° at 12:00 UTC)
    const tanDec = Math.tan(dec * rad);
    const closePole = dec > 0 ? -90 : 90; // δ>0 (northern summer) → the south pole is in night
    const ring = [];
    for (let lng = -180; lng <= 180; lng += 2) {
        let lat;
        if (Math.abs(tanDec) < 1e-6) {
            // equinox: terminator is the λs±90 meridian → night hemisphere is a full column
            lat = Math.cos((lng - subsolarLng) * rad) > 0 ? closePole : -closePole;
        }
        else {
            lat = Math.atan(-Math.cos((lng - subsolarLng) * rad) / tanDec) / rad;
        }
        ring.push([lng, Math.max(-89.5, Math.min(89.5, lat))]);
    }
    ring.push([180, closePole], [-180, closePole]); // close along the dark pole
    return ring;
}
// ── basemap: self-hosted Protomaps PMTiles on R2 ──
// One vector archive (OpenStreetMap via Protomaps, zoom 0-10, ~4 GB) on our own R2
// bucket behind a custom domain: no key, no third-party quota, zero egress, and the
// browser fetches it by HTTP range through the pmtiles protocol. Rendered with the
// Protomaps flavours (light / dark) minus every symbol layer, so the basemap stays
// label-less: city names are the one-language deck TextLayer below, as before.
// The zoom-12 view over-zooms the zoom-10 tiles, which is plenty for placing a site.
const BASEMAP_URL = 'https://tiles.vitesseautomation.ai/world.pmtiles';
const BASEMAP_SOURCE = 'protomaps';
let pmtilesRegistered = false;
/** The pmtiles:// protocol is process-wide in maplibre; register it once. */
function ensurePmtilesProtocol() {
    if (pmtilesRegistered)
        return;
    maplibregl.addProtocol('pmtiles', new PmtilesProtocol().tile);
    pmtilesRegistered = true;
}
/** Normalised Web-Mercator Y (0 = +85°N … 1 = −85°S) — places lat onto the minimap. */
function mercatorYNorm(lat) {
    const c = Math.max(-85.051, Math.min(85.051, lat));
    return (1 - Math.asinh(Math.tan((c * Math.PI) / 180)) / Math.PI) / 2;
}
function buildStyle() {
    ensurePmtilesProtocol();
    const flavor = namedFlavor(getResolvedTheme() === 'light' ? 'light' : 'dark');
    // Drop the symbol layers (place names, road shields, POIs): the one-language
    // city labels are drawn by deck, and without symbols the style needs no glyphs
    // or sprite — nothing else to host.
    const layers = basemapLayers(BASEMAP_SOURCE, flavor, { lang: 'en' }).filter((l) => l.type !== 'symbol');
    return {
        version: 8,
        sources: {
            [BASEMAP_SOURCE]: { type: 'vector', url: `pmtiles://${BASEMAP_URL}`, attribution: '© OpenStreetMap' },
        },
        layers,
    };
}
/** Zoom at/above which co-located groups EXPAND (spiderfy) into individual chips; below
 *  it they collapse to one cluster chip. Clicking a cluster eases the camera past this. */
const EXPAND_ZOOM = 9;
/** {lat,lng} → maplibre/deck [lng,lat] tuple. */
const llt = (p) => [p.lng, p.lat];
/** Cubic ease-in-out — slow, fast, slow. The camera glide every click-to-zoom uses,
 *  so approaching a gateway feels like a deliberate move, not a snap. */
const easeCubicInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
let VitesseWorldMap = class VitesseWorldMap extends LitElement {
    constructor() {
        super();
        this.gateways = [];
        /** orgId → human name for the hover tooltip — never the raw org id. */
        this.orgNames = new Map();
        /** Private cloud servers (our Hetzner boxes) — distinct hub nodes. */
        this.cloudServers = [];
        /** Custom labelled points (lat/lng) — rendered as PIN chips (pointed bottom,
         *  label beside), visually kin to the gateway/cloud chips but clearly "a place
         *  someone marked", not a device. The dashboards AI authors these. */
        this.markers = [];
        /** Fingerprint of the open/selected gateway ('' = none) — haloed on the map. */
        this.selectedFp = '';
        /** Fixed pixel height, or 0 (default) to FILL the container — the full-page view. */
        this.height = 0;
        /** Day/night terminator shade — OFF by default; toggled in the HUD. */
        this.night = false;
        /** Global map-nav hotkeys (+/− arrows F W N). ON for a dedicated map page; a host
         *  embedding the map as ONE tile among many (the dashboards widget) turns them off
         *  so page-wide keys aren't hijacked by a single tile. */
        this.hotkeys = true;
        this.boundKey = (e) => this.onGlobalKey(e);
        this.plotSig = '';
        /** Gateway + cloud-hub DOM markers (the .fleet-*-chip component — filled glyph + CSS
         *  glow + CSS heartbeat ping) keyed by id — diffed in sync{Gateway,Cloud}Markers, NOT a
         *  deck layer, so the chips stay vector-sharp and glow cleanly. Cluster key = `cluster:<k>`. */
        this.cloudMarkers = new Map();
        this.gatewayMarkers = new Map();
        this.pinMarkers = new Map();
        new LocaleController(this); // re-render the legend on language switch
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.hotkeys)
            window.addEventListener('keydown', this.boundKey); // +/− zoom · arrows pan · F/W/N nav
    }
    createRenderRoot() {
        return this;
    }
    firstUpdated() {
        const el = this.querySelector('.fleet-map-canvas');
        if (!el)
            return;
        // Defer map creation until the container has a real size. A maplibre map
        // created into a 0×0 container never fires `load`, so its style, deck
        // layers, world-fit and markers never initialise — and it stays blank even
        // after the container grows. That is exactly what a dashboard tile (which
        // lays out after the board body arrives, inside a gate-overlay) produces.
        if (el.clientWidth > 0 && el.clientHeight > 0) {
            this.initMap(el);
            return;
        }
        this.bootRo = new ResizeObserver(() => {
            if (el.clientWidth > 0 && el.clientHeight > 0) {
                this.bootRo?.disconnect();
                this.bootRo = undefined;
                this.initMap(el);
            }
        });
        this.bootRo.observe(el);
    }
    initMap(el) {
        const map = new maplibregl.Map({
            container: el,
            style: buildStyle(),
            center: [5, 25],
            zoom: 1.4,
            minZoom: 1,
            maxZoom: 12,
            attributionControl: false, // internal admin surface — no chrome
            dragRotate: false, // 2D, minimalist — no tilt/rotate
            renderWorldCopies: false, // one world — no wrapped copies (cleaner; no seam fills)
            trackResize: false, // OUR debounced observer below resizes — maplibre's own would clear the canvas every animation frame of a rail/panel slide
        });
        this.map = map;
        const overlay = new MapboxOverlay({ layers: [] });
        map.addControl(overlay);
        this.overlay = overlay;
        // The first-paint setup. Idempotent (the `ready` guard) so it is safe to run
        // from BOTH the load event and the fallback below.
        let ready = false;
        const onReady = () => {
            if (ready)
                return;
            ready = true;
            this.renderLayers(); // terminator + city labels (the only deck layers)
            this.fitWorld(); // sets the initial view — this is what forces the first render
            this.syncCloudMarkers(); // blue hub DOM markers
            this.syncGatewayMarkers(); // green/yellow/red gateway DOM markers (same component)
            this.syncPinMarkers(); // custom labelled points
            this.updateMinimapFrame();
        };
        map.on('load', onReady);
        map.on('move', () => {
            this.renderLayers(); // re-cull city labels for the new viewport
            this.syncGatewayMarkers(); // re-fan / collapse↔expand the chips for the new zoom
            this.updateMinimapFrame(); // the minimap's viewport rectangle follows
        });
        requestAnimationFrame(() => map.resize());
        // A dashboard tile can gain its height a frame or two AFTER the map mounts
        // (the tile lays out once the board body arrives, and a gate-overlay wrapper
        // sizes it late). A single resize can fire while the container is still 0×0,
        // and with trackResize off the map then never requests a tile. A few trailing
        // resizes catch the late layout without the per-frame cost of trackResize.
        // Deck.gl's overlay can hold map.loaded() false so the load event never
        // settles inside a late-laid-out tile. Force it: trailing resizes, and if the
        // map still hasn't run its ready-setup, run it ourselves (fitWorld sets the
        // view, which triggers the first paint).
        // Force the raster canvas to actually PAINT. In a tile that laid out after
        // the map mounted, resize + fitWorld set the view but leave the GL canvas
        // blank — the map never reports `loaded` (the deck overlay holds it un-idle),
        // so its render loop idles and the raster is never drawn. A sub-perceptible
        // zoom delta invalidates the tile cache and forces the draw. The exact frame
        // the GL context is ready varies, so kick a handful of times over ~2.7s
        // (alternating delta → net-zero zoom), then stop so it never fights the user.
        let tries = 0;
        const kick = () => {
            map.resize();
            if (!ready) {
                try {
                    onReady();
                }
                catch {
                    ready = false;
                }
            }
            if (ready) {
                // jumpTo (a FULL camera set), not setZoom — only a full camera update
                // forces the raster to redraw here; setZoom/fitWorld leave the GL canvas
                // blank. Sub-perceptible center+zoom delta; done a few times because the
                // frame the context is ready varies.
                const c = map.getCenter();
                map.jumpTo({ center: [c.lng + 0.0002, c.lat], zoom: map.getZoom() + 0.0002 });
                map.triggerRepaint?.();
            }
            if (++tries < 12)
                window.setTimeout(kick, 200);
        };
        window.setTimeout(kick, 120);
        this.themeUnsub = onThemeChange(() => this.applyTheme());
        // Debounced-trailing resize. A rail/panel animating the container fires the
        // observer every frame; calling map.resize() per event resizes (= CLEARS) the
        // canvas buffer each time — the map goes blank for the whole animation. Instead
        // the CSS below stretches the LAST PAINTED FRAME while the container moves
        // (smooth, slightly soft), and one crisp buffer resize lands when it settles.
        this.ro = new ResizeObserver(() => {
            clearTimeout(this.resizeDebounce);
            this.resizeDebounce = window.setTimeout(() => map.resize(), 120);
        });
        this.ro.observe(el);
        this.terminatorTimer = window.setInterval(() => {
            this.renderLayers(); // drift the deck terminator
            if (this.night)
                this.requestUpdate(); // and the minimap's night overlay
        }, 60_000);
    }
    updated(changed) {
        if (!this.map?.loaded())
            return;
        if (changed.has('gateways')) {
            // Re-render the chips only on a MAP-relevant change (membership / status / position) —
            // NOT the 1 Hz health refresh (cpu/mem aren't in the signature, so no needless redraw).
            const sig = this.gateways.map((g) => `${g.deviceFingerprint}:${g.status}:${g.lat}:${g.lng}`).join('|');
            if (sig !== this.plotSig) {
                this.plotSig = sig;
                this.syncGatewayMarkers(); // membership / status / position changed → re-sync chips
            }
        }
        else if (changed.has('cloudServers')) {
            this.syncCloudMarkers();
        }
        else if (changed.has('markers')) {
            this.syncPinMarkers();
        }
        else if (changed.has('selectedFp')) {
            this.syncGatewayMarkers(); // toggle the .is-selected ring on the open gateway's chip
        }
        this.updateMinimapFrame(); // reposition the viewport rect after any template re-render
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('keydown', this.boundKey);
        if (this.terminatorTimer !== undefined)
            clearInterval(this.terminatorTimer);
        clearTimeout(this.resizeDebounce);
        this.ro?.disconnect();
        this.bootRo?.disconnect();
        this.themeUnsub?.();
        this.hoverPopup?.remove();
        for (const m of this.cloudMarkers.values())
            m.remove();
        this.cloudMarkers.clear();
        for (const m of this.gatewayMarkers.values())
            m.remove();
        this.gatewayMarkers.clear();
        for (const m of this.pinMarkers.values())
            m.remove();
        this.pinMarkers.clear();
        this.map?.remove();
        this.map = undefined;
    }
    // ── map-nav hotkeys ──
    onGlobalKey(e) {
        const active = document.activeElement;
        const typing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
        // Map-nav hotkeys — only when NOT typing and no modifier (browser shortcuts + any
        // field typing untouched). +/− shoot the zoom, arrows pan, F frames the fleet,
        // W resets the world view, N toggles day/night. Mirrors the HUD control cluster 1:1.
        if (typing || e.metaKey || e.ctrlKey || e.altKey)
            return;
        switch (e.key) {
            case '+':
            case '=':
                e.preventDefault();
                this.zoomIn();
                break;
            case '-':
            case '_':
                e.preventDefault();
                this.zoomOut();
                break;
            case 'f':
            case 'F':
                e.preventDefault();
                this.fitFleet();
                break;
            case 'w':
            case 'W':
                e.preventDefault();
                this.fitWorld();
                break;
            case 'n':
            case 'N':
                e.preventDefault();
                this.toggleNight();
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.map?.panBy([0, -130]);
                break;
            case 'ArrowDown':
                e.preventDefault();
                this.map?.panBy([0, 130]);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                this.map?.panBy([-130, 0]);
                break;
            case 'ArrowRight':
                e.preventDefault();
                this.map?.panBy([130, 0]);
                break;
        }
    }
    openGateway(g) {
        this.dispatchEvent(new CustomEvent('gateway-open', {
            detail: { fingerprint: g.deviceFingerprint },
            bubbles: true,
            composed: true,
        }));
    }
    /** Lay out the fleet for the CURRENT zoom: co-located gateways (same public IP → same
     *  resolved geo) COLLAPSE into one circle-pile cluster node when zoomed out, and EXPAND
     *  (spiderfy) into individual chips at/above EXPAND_ZOOM — or when the user clicks the
     *  cluster (which eases the camera past EXPAND_ZOOM). Expanded members fan onto a
     *  phyllotaxis disc in SCREEN space (golden-angle spiral, even density for ANY N) via
     *  project→offset→unproject, so the spread is a fixed pixel size at every zoom. Solo
     *  gateways are always their own chip. Returns the chips to draw (+ their display
     *  positions) and the collapsed clusters. */
    clusterLayout() {
        const posByFp = new Map();
        const chips = [];
        const clusters = [];
        if (!this.map)
            return { posByFp, chips, clusters };
        const expanded = this.map.getZoom() >= EXPAND_ZOOM;
        const groups = new Map();
        for (const g of this.gateways) {
            const p = geoToLatLng(g);
            const key = `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`; // ~110 m bucket — same-IP geo is identical
            const grp = groups.get(key);
            if (grp)
                grp.push(g);
            else
                groups.set(key, [g]);
        }
        const GOLDEN = Math.PI * (3 - Math.sqrt(5)); // ~137.5° — the phyllotaxis angle
        const STEP = 22; // px between successive disc points (chips are ~30px → distinct, lightly overlapping)
        for (const [key, grp] of groups) {
            if (grp.length === 1) {
                posByFp.set(grp[0].deviceFingerprint, llt(geoToLatLng(grp[0])));
                chips.push(grp[0]);
                continue;
            }
            if (!expanded) {
                const c = geoToLatLng(grp[0]);
                clusters.push({ key, lng: c.lng, lat: c.lat, count: grp.length, online: grp.some((g) => g.status === 'online') });
                continue;
            }
            const c = this.map.project(llt(geoToLatLng(grp[0]))); // shared screen centre
            grp.forEach((g, i) => {
                const r = STEP * Math.sqrt(i + 0.5); // sqrt → even-density disc, grows with N
                const a = i * GOLDEN;
                const ll = this.map.unproject([c.x + r * Math.cos(a), c.y + r * Math.sin(a)]);
                posByFp.set(g.deviceFingerprint, [ll.lng, ll.lat]);
                chips.push(g);
            });
        }
        return { posByFp, chips, clusters };
    }
    /** Rebuild the deck layers — JUST the ambient day/night terminator + the one-language
     *  city labels. Gateways and cloud hubs are DOM markers (sync{Gateway,Cloud}Markers),
     *  NOT deck layers. Runs on every map move (to re-cull labels for the new viewport). */
    renderLayers() {
        if (!this.overlay || !this.map)
            return;
        const b = this.map.getBounds();
        const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
        const grey = hexRGB(token('--text-tertiary')); // city labels (#6b7280, identical both themes)
        // City labels — ONE-LANGUAGE place names (the basemap is label-less to avoid the
        // multi-script clutter), zoom-gated by tier so the world overview stays clean and
        // names fade in as you zoom. Candidates = biggest-in-view above the zoom's population
        // floor (CITIES is pop-sorted), viewport-culled; then a greedy screen-space de-overlap
        // keeps the largest and drops any whose label box would collide.
        const z = this.map.getZoom();
        const minPop = popThreshold(z);
        const candidates = z < 2.2
            ? []
            : CITIES.filter((c) => c.pop >= minPop && c.lng >= bbox[0] && c.lng <= bbox[2] && c.lat >= bbox[1] && c.lat <= bbox[3]).slice(0, 220);
        const placed = [];
        const cityData = [];
        for (const c of candidates) {
            const p = this.map.project([c.lng, c.lat]);
            if (placed.every((q) => Math.abs(q.x - p.x) > 70 || Math.abs(q.y - p.y) > 15)) {
                placed.push({ x: p.x, y: p.y });
                cityData.push(c);
                if (cityData.length >= 55)
                    break;
            }
        }
        const cityLabelLayer = new TextLayer({
            id: 'city-labels',
            data: cityData,
            getPosition: (c) => [c.lng, c.lat],
            getText: (c) => c.name,
            getSize: 12,
            sizeUnits: 'pixels',
            getColor: [grey[0], grey[1], grey[2], 255], // --text-tertiary: grey in BOTH themes
            fontFamily: appFont(),
            fontWeight: 400,
            getTextAnchor: 'start',
            getAlignmentBaseline: 'center',
            getPixelOffset: [7, 0],
            fontSettings: { fontSize: 52 },
        });
        // Day/night terminator — an ambient dark shade over the night half. Recomputed each
        // render + on a 60s idle timer; the sun drifts ~0.25°/min, so it stays current.
        const terminatorLayer = this.night
            ? new PolygonLayer({
                id: 'terminator',
                data: [{ ring: nightPolygon(new Date()) }],
                getPolygon: (d) => d.ring,
                filled: true,
                getFillColor: [6, 10, 28, 76],
                stroked: true,
                getLineColor: [120, 150, 210, 30],
                getLineWidth: 1,
                lineWidthUnits: 'pixels',
            })
            : null;
        this.overlay.setProps({ layers: [terminatorLayer, cityLabelLayer] });
    }
    /** DOM markers for the GATEWAYS — the EXACT same chip component as the cloud hub
     *  (.fleet-gateway-chip mirrors .fleet-cloud-chip), only the COLOUR differs by status
     *  (green online / yellow buffer / red offline; red doesn't ping). Co-located boxes
     *  COLLAPSE to one cluster chip when zoomed out and FAN into individual chips at/above
     *  EXPAND_ZOOM (clusterLayout). Re-run on every move so the fan + collapse/expand track
     *  the live zoom. Diffed by id (fp for chips, `cluster:<key>` for clusters) so pan/zoom
     *  updates position + colour without recreating (no flicker). */
    syncGatewayMarkers() {
        if (!this.map)
            return;
        const { posByFp, chips, clusters } = this.clusterLayout();
        const seen = new Set();
        // Reuse the marker for an id (just move it), else create the chip DOM once. maplibre
        // sets `transform` on the marker el for positioning, so the chip (hover-scale +
        // ::after ping) is the CHILD — my classes never fight maplibre's transform.
        const chipEl = (id, lng, lat) => {
            let m = this.gatewayMarkers.get(id);
            if (m) {
                m.setLngLat([lng, lat]);
            }
            else {
                const el = document.createElement('div');
                el.className = 'fleet-gateway-marker';
                el.innerHTML =
                    '<div class="fleet-gateway-chip"><vitesse-icon name="hard-drive" size="13"></vitesse-icon></div>';
                m = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(this.map);
                this.gatewayMarkers.set(id, m);
            }
            return m.getElement().firstElementChild; // the .fleet-gateway-chip
        };
        // Individual chips (solo gateways + the expanded members of co-located groups).
        for (const g of chips) {
            const id = g.deviceFingerprint;
            seen.add(id);
            const pos = posByFp.get(id) ?? llt(geoToLatLng(g));
            const chip = chipEl(id, pos[0], pos[1]);
            chip.className = `fleet-gateway-chip is-${g.status}${id === this.selectedFp ? ' is-selected' : ''}`;
            const el = chip.parentElement;
            el.onclick = () => {
                this.hoverPopup?.remove();
                // Glide INTO the box — slow-fast-slow — before/while the host reacts. Never
                // zoom OUT to reach a chip that's already close.
                this.map?.easeTo({
                    center: pos,
                    zoom: Math.max(this.map.getZoom(), EXPAND_ZOOM + 0.6),
                    duration: 1100,
                    easing: easeCubicInOut,
                });
                this.openGateway(g);
            };
            el.onmouseenter = () => this.showHoverPopup(pos[0], pos[1], this.gatewayTipHtml(g));
            el.onmouseleave = () => this.hoverPopup?.remove();
        }
        // Collapsed clusters — ONE chip, status rolled up (green if any member online, else
        // red), no count. Click → ease the camera past EXPAND_ZOOM (re-renders as the members).
        for (const c of clusters) {
            const id = `cluster:${c.key}`;
            seen.add(id);
            const chip = chipEl(id, c.lng, c.lat);
            chip.className = `fleet-gateway-chip is-${c.online ? 'online' : 'offline'}`;
            const el = chip.parentElement;
            el.onclick = () => {
                this.hoverPopup?.remove();
                this.map?.easeTo({ center: [c.lng, c.lat], zoom: EXPAND_ZOOM + 0.6, duration: 1100, easing: easeCubicInOut });
            };
            el.onmouseenter = () => this.showHoverPopup(c.lng, c.lat, `<span class="fmt-name">${t('worldmap.clickToOpen')}</span>`);
            el.onmouseleave = () => this.hoverPopup?.remove();
        }
        // Drop departed markers (gateway gone, or a cluster↔chips swap on a zoom threshold).
        for (const [id, marker] of this.gatewayMarkers) {
            if (!seen.has(id)) {
                marker.remove();
                this.gatewayMarkers.delete(id);
            }
        }
    }
    /** Custom labelled PIN markers — the teardrop chip (pointed bottom = "a place
     *  someone marked") with its label chip beside it. Same visual family as the
     *  gateway/cloud chips, no health, no click behaviour. Keyed by label+coords and
     *  diffed like the others so live edits (the AI adding one) never flicker the rest.
     *  Anchored at the BOTTOM tip, the way a physical pin sits on its spot. */
    syncPinMarkers() {
        if (!this.map)
            return;
        const seen = new Set();
        for (const p of this.markers) {
            if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng))
                continue;
            const id = `${p.label}\u0000${p.lat}\u0000${p.lng}`;
            seen.add(id);
            if (this.pinMarkers.has(id))
                continue; // identity-keyed: an unchanged pin needs nothing
            const el = document.createElement('div');
            el.className = 'fleet-pin-marker';
            const chip = document.createElement('div');
            chip.className = 'fleet-pin-chip';
            chip.innerHTML = '<vitesse-icon name="map-pin" size="12"></vitesse-icon>';
            const label = document.createElement('div');
            label.className = 'fleet-pin-label';
            label.textContent = p.label;
            el.append(chip, label);
            this.pinMarkers.set(id, new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat([p.lng, p.lat]).addTo(this.map));
        }
        for (const [id, marker] of this.pinMarkers) {
            if (!seen.has(id)) {
                marker.remove();
                this.pinMarkers.delete(id);
            }
        }
    }
    /** Shared glass hover popup (same look as the cloud-hub popup) — used by both gateway
     *  and cloud markers. One instance, repositioned + re-filled on each hover. */
    showHoverPopup(lng, lat, html) {
        if (!this.map)
            return;
        this.hoverPopup ??= new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 16,
            className: 'fleet-cloud-popup',
        });
        this.hoverPopup.setLngLat([lng, lat]).setHTML(html).addTo(this.map);
    }
    /** DOM markers for the cloud hubs — the .fleet-cloud-chip component (blue, always-on,
     *  CSS-pulsing). Diffed by id so the ~1 Hz refresh updates position without recreating
     *  (no flicker); a departed server's marker is removed. */
    syncCloudMarkers() {
        if (!this.map)
            return;
        const seen = new Set();
        for (const c of this.cloudServers) {
            seen.add(c.id);
            const existing = this.cloudMarkers.get(c.id);
            if (existing) {
                existing.setLngLat([c.lng, c.lat]); // geo can shift if it re-resolves
                continue;
            }
            // maplibre sets `transform` on the marker element for positioning, so the chip
            // (hover-scale + ::after pulse ring) is a CHILD — never transform the marker itself.
            const el = document.createElement('div');
            el.className = 'fleet-cloud-marker';
            el.innerHTML = '<div class="fleet-cloud-chip"><vitesse-icon name="server" size="15"></vitesse-icon></div>';
            // Hover → the shared glass popup; reads the LATEST record by id so it tracks refresh.
            el.addEventListener('mouseenter', () => {
                const cur = this.cloudServers.find((x) => x.id === c.id);
                if (cur)
                    this.showHoverPopup(cur.lng, cur.lat, this.cloudTipHtml(cur));
            });
            el.addEventListener('mouseleave', () => this.hoverPopup?.remove());
            // Click → open the server detail panel (mirrors a gateway click → gateway panel).
            el.addEventListener('click', () => {
                this.hoverPopup?.remove();
                this.dispatchEvent(new CustomEvent('cloud-open', { detail: { id: c.id }, bubbles: true, composed: true }));
            });
            this.cloudMarkers.set(c.id, new maplibregl.Marker({ element: el }).setLngLat([c.lng, c.lat]).addTo(this.map));
        }
        for (const [id, marker] of this.cloudMarkers) {
            if (!seen.has(id)) {
                marker.remove();
                this.cloudMarkers.delete(id);
            }
        }
    }
    /** Cloud-hub hover popup — JUST name + location · "Private Server". A quick peek; the
     *  full live dials + host stats live in the click-opened server panel. */
    cloudTipHtml(c) {
        return `<span class="fmt-name">${esc(c.name)}</span><span class="fmt-org">${esc(c.geo)} · ${esc(t('worldmap.cloud'))}</span>`;
    }
    /** Gateway hover popup — name + owner org (or "unclaimed"). The full live dials live in
     *  the click-opened gateway panel. */
    gatewayTipHtml(g) {
        const name = g.displayName || g.deviceFingerprint.slice(0, 12);
        const org = g.orgId ? (this.orgNames.get(g.orgId) ?? g.orgId) : t('worldmap.unclaimed');
        return `<span class="fmt-name">${esc(name)}</span><span class="fmt-org">${esc(org)}</span>`;
    }
    /** Default view = the WHOLE inhabited world (zoomed out), framed to the viewport —
     *  not zoomed to the fleet. A future "fit to fleet" control can zoom in on demand. */
    fitWorld() {
        this.map?.fitBounds([
            [-180, -58],
            [180, 74],
        ], { padding: 16, duration: 0 });
    }
    zoomIn() {
        this.map?.zoomIn({ duration: 300 });
    }
    zoomOut() {
        this.map?.zoomOut({ duration: 300 });
    }
    /** Toggle the day/night terminator shade. */
    toggleNight() {
        this.night = !this.night;
        this.renderLayers();
    }
    /** Minimap click → recenter the main map there, KEEPING the current zoom (easeTo center
     *  only). The thumbnail is the z0 Web-Mercator world, so invert Mercator for the lat. */
    onMinimapClick(e) {
        if (!this.map)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width; // 0..1 (left→right = -180→180)
        const y = (e.clientY - rect.top) / rect.height; // 0..1 (top→bottom = +85→-85)
        const lng = x * 360 - 180;
        const lat = (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
        this.map.easeTo({ center: [lng, lat], duration: 400 });
    }
    /** Position the minimap's viewport rectangle = where the main map currently looks,
     *  projected onto the z0 world thumbnail. Imperative (called on move) so panning never
     *  re-renders the Lit template. */
    updateMinimapFrame() {
        const frame = this.querySelector('.fleet-map-minimap-frame');
        if (!frame || !this.map)
            return;
        const b = this.map.getBounds();
        const x0 = Math.max(0, Math.min(1, (b.getWest() + 180) / 360));
        const x1 = Math.max(0, Math.min(1, (b.getEast() + 180) / 360));
        const y0 = mercatorYNorm(b.getNorth());
        const y1 = mercatorYNorm(b.getSouth());
        frame.style.left = `${x0 * 100}%`;
        frame.style.top = `${y0 * 100}%`;
        frame.style.width = `${Math.max(3, (x1 - x0) * 100)}%`;
        frame.style.height = `${Math.max(3, (y1 - y0) * 100)}%`;
    }
    /** The solar terminator's night region as an SVG polygon points string in the minimap's
     *  0–100 space — same projection as the z0 tile (lng→x linear, lat→Mercator y), so the
     *  shaded half lines up with the thumbnail. Mirrors the deck terminatorLayer for the HUD. */
    minimapNightPoints() {
        return nightPolygon(new Date())
            .map(([lng, lat]) => `${(((lng + 180) / 360) * 100).toFixed(2)},${(mercatorYNorm(lat) * 100).toFixed(2)}`)
            .join(' ');
    }
    /** "Frame the fleet" — fit all gateways + cloud servers in view (null-island points
     *  dropped). Empty fleet → the world view. */
    fitFleet() {
        if (!this.map)
            return;
        const pts = [];
        for (const g of this.gateways) {
            const p = geoToLatLng(g);
            if (Math.abs(p.lat) > 0.01 || Math.abs(p.lng) > 0.01)
                pts.push([p.lng, p.lat]);
        }
        for (const c of this.cloudServers)
            pts.push([c.lng, c.lat]);
        for (const m of this.markers)
            pts.push([m.lng, m.lat]);
        if (!pts.length)
            return this.fitWorld();
        let minLng = 180;
        let minLat = 90;
        let maxLng = -180;
        let maxLat = -90;
        for (const [lng, lat] of pts) {
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
        }
        this.map.fitBounds([
            [minLng, minLat],
            [maxLng, maxLat],
        ], { padding: 90, maxZoom: 7, duration: 700 });
    }
    /** Theme toggle → rebuild the basemap with the new token colours + re-render the
     *  deck layers (setStyle drops them). */
    applyTheme() {
        if (!this.map)
            return;
        this.map.setStyle(buildStyle());
        this.map.once('styledata', () => this.renderLayers());
    }
    render() {
        const canvasStyle = this.height > 0 ? `height:${this.height}px` : 'height:100%';
        // ONE attached HUD component: minimap on the LEFT, the zoom/nav buttons stacked on the
        // RIGHT, cleanly separated by a divider, both the SAME vertical height. Minimalist.
        return html `
      <div class="fleet-map-shell">
        <div class="fleet-map-canvas" style=${canvasStyle}></div>
        <!-- OpenStreetMap's licence asks for visible credit wherever its data is drawn. -->
        <a class="fleet-map-credit" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap</a>
        <div class="fleet-map-hud">
          <button class="fleet-map-minimap" title=${t('worldmap.minimap')} @click=${(e) => this.onMinimapClick(e)}>
            <svg class="fleet-map-minimap-world" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d=${WORLD_LAND_PATH}></path>
            </svg>
            ${this.night
            ? html `<svg class="fleet-map-minimap-night" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <polygon points=${this.minimapNightPoints()}></polygon>
                </svg>`
            : nothing}
            <span class="fleet-map-minimap-frame"></span>
          </button>
          <div class="fleet-map-hud-sep"></div>
          <div class="fleet-map-hud-buttons">
            <button class="base-icon-button" title=${`${t('worldmap.zoomIn')} · +`} @click=${() => this.zoomIn()}>
              <vitesse-icon name="plus" size="16"></vitesse-icon>
            </button>
            <button class="base-icon-button" title=${`${t('worldmap.zoomOut')} · −`} @click=${() => this.zoomOut()}>
              <vitesse-icon name="minus" size="16"></vitesse-icon>
            </button>
            <button
              class="base-icon-button"
              title=${`${t('worldmap.worldView')} · W · (F frames the fleet)`}
              @click=${() => this.fitWorld()}
            >
              <vitesse-icon name="globe" size="16"></vitesse-icon>
            </button>
            <button
              class="base-icon-button ${this.night ? 'active' : ''}"
              title=${`${t('worldmap.dayNight')} · N`}
              @click=${() => this.toggleNight()}
            >
              <vitesse-icon name="moon" size="16"></vitesse-icon>
            </button>
          </div>
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseWorldMap.prototype, "gateways", void 0);
__decorate([
    property({ attribute: false })
], VitesseWorldMap.prototype, "orgNames", void 0);
__decorate([
    property({ attribute: false })
], VitesseWorldMap.prototype, "cloudServers", void 0);
__decorate([
    property({ attribute: false })
], VitesseWorldMap.prototype, "markers", void 0);
__decorate([
    property()
], VitesseWorldMap.prototype, "selectedFp", void 0);
__decorate([
    property({ type: Number })
], VitesseWorldMap.prototype, "height", void 0);
__decorate([
    state()
], VitesseWorldMap.prototype, "night", void 0);
__decorate([
    property({ type: Boolean })
], VitesseWorldMap.prototype, "hotkeys", void 0);
VitesseWorldMap = __decorate([
    customElement('vitesse-world-map')
], VitesseWorldMap);
export { VitesseWorldMap };
//# sourceMappingURL=vitesse-world-map.js.map